import { ChevronDown } from '@untitledui/icons';
import { type SelectHTMLAttributes, useId } from 'react';

import { HintText } from '@/lib/base/forms/hint-text';
import { Label } from '@/lib/base/forms/input/label';
import type { SelectSize } from '@/lib/base/forms/select/select';
import { sizes } from '@/lib/base/forms/select/select-shared';
import { cx } from '@/utils/cx';

// Size configuration matching the library Select sizing system: `select` is
// built from `sizes[step].root` in select-shared.ts (including its text-size
// class — unlike TextArea/Input, a <select> has no separate base text class to
// fall back on), so a NativeSelect and a Select at the same `size` render the
// same height. The only additions are the `pr-*` overrides reserving room for
// the absolutely-positioned chevron (the library Select lays its chevron out in
// flow instead) and the chevron sizing itself, which mirrors CHEVRON_SIZE_CLASSES.
const nativeSelectSizes: Record<
  SelectSize,
  { select: string; chevron: string; chevronPos: string }
> = {
  xs: {
    select: cx(sizes.xs.root, 'pr-7'),
    chevron: 'size-4 stroke-[2.5px]',
    chevronPos: 'right-2.5',
  },
  sm: {
    select: cx(sizes.sm.root, 'pr-8'),
    chevron: 'size-4 stroke-[2.5px]',
    chevronPos: 'right-3',
  },
  // `md` is responsive: it renders at compact (`sm`) dimensions on mobile and
  // scales up to its full size at the `sm` breakpoint. Mobile responsiveness
  // is built into the size variant so callers don't have to branch on viewport.
  md: {
    select: cx(sizes.md.root, 'pr-8 sm:pr-10'),
    chevron: 'size-4 stroke-[2.5px] sm:size-5 sm:stroke-2',
    chevronPos: 'right-3 sm:right-3.5',
  },
  lg: {
    select: cx(sizes.lg.root, 'pr-10'),
    chevron: 'size-5 stroke-2',
    chevronPos: 'right-4',
  },
  xl: {
    select: cx(sizes.xl.root, 'pr-11'),
    chevron: 'size-5 stroke-2',
    chevronPos: 'right-5',
  },
};

interface NativeSelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> {
  label?: string;
  hint?: string;
  selectClassName?: string;
  options: readonly { label: string; value: string; disabled?: boolean }[];
  /** Controls padding, font size, and chevron sizing. Defaults to 'sm'. */
  size?: SelectSize;
}

export const NativeSelect = ({
  label,
  hint,
  options,
  className,
  selectClassName,
  size = 'sm',
  ...props
}: NativeSelectProps) => {
  const generatedId = useId();
  const selectId = props.id ?? `select-native-${generatedId}`;
  const labelId = `select-native-label-${generatedId}`;
  const hintId = `select-native-hint-${generatedId}`;

  const sizeConfig = nativeSelectSizes[size];

  return (
    <div
      className={cx(
        'w-full in-data-input-wrapper:w-max',
        // Inside an InputGroup the select must fill the group's height. That
        // requires an unbroken `height: 100%` chain from the group down to the
        // <select>; without these the select resolves `h-full` against an
        // auto-height ancestor and collapses to its content height, sitting
        // short of the sibling input. Callers used to pin it with `!h-10`,
        // which then broke every responsive size step.
        'in-data-input-wrapper:h-full',
        className
      )}
    >
      {label && (
        <Label htmlFor={selectId} id={labelId} className='mb-1.5'>
          {label}
        </Label>
      )}

      {/* Alignment stays `items-center` for ALL children: the chevron is
          absolutely positioned but sets only `right-*`, so its vertical
          placement comes from its static position as a grid item. Switching the
          container to `items-stretch` pins the chevron to the top of the field.
          Only the <select> stretches, via `self-stretch` below. */}
      <div className='relative grid w-full items-center in-data-input-wrapper:h-full'>
        <select
          {...props}
          id={selectId}
          aria-describedby={hint ? hintId : undefined}
          aria-labelledby={label ? labelId : undefined}
          className={cx(
            'appearance-none rounded-lg bg-primary text-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset placeholder:text-fg-quaternary focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:bg-disabled_subtle disabled:text-disabled',
            sizeConfig.select,
            // Styles when the select is within an `InputGroup`. The group draws
            // the focus ring for the whole field, so the select suppresses its
            // own focus-visible ring here to avoid a detached/doubled ring.
            'in-data-input-wrapper:flex in-data-input-wrapper:h-full in-data-input-wrapper:self-stretch in-data-input-wrapper:gap-1 in-data-input-wrapper:bg-inherit in-data-input-wrapper:px-3 in-data-input-wrapper:py-2 in-data-input-wrapper:font-normal in-data-input-wrapper:text-tertiary in-data-input-wrapper:shadow-none in-data-input-wrapper:ring-transparent in-data-input-wrapper:focus-visible:ring-transparent',
            // Styles for the select when `TextField` is disabled
            'in-data-input-wrapper:group-disabled:pointer-events-none in-data-input-wrapper:group-disabled:cursor-not-allowed in-data-input-wrapper:group-disabled:bg-transparent in-data-input-wrapper:group-disabled:text-disabled',
            // Common styles for sizes and border radius within `InputGroup`
            // Every size step the group can take needs its own override of
            // the `py-2` above, or the select keeps the `sm` height and
            // stretches the whole group past its input (xs: 36px vs 32px).
            'in-data-input-wrapper:in-data-leading:rounded-r-none in-data-input-wrapper:in-data-trailing:rounded-l-none in-data-input-wrapper:in-data-[input-size=md]:py-2.5 in-data-input-wrapper:in-data-leading:in-data-[input-size=md]:pl-3.5 in-data-input-wrapper:in-data-[input-size=sm]:py-2 in-data-input-wrapper:in-data-[input-size=sm]:pl-3 in-data-input-wrapper:in-data-[input-size=xs]:py-1.5 in-data-input-wrapper:in-data-[input-size=xs]:pl-2.5',
            // For "leading" dropdown within `InputGroup`
            'in-data-input-wrapper:in-data-leading:in-data-[input-size=md]:pr-4.5 in-data-input-wrapper:in-data-leading:in-data-[input-size=sm]:pr-4.5 in-data-input-wrapper:in-data-leading:in-data-[input-size=xs]:pr-4',
            // For "trailing" dropdown within `InputGroup`
            'in-data-input-wrapper:in-data-trailing:in-data-[input-size=md]:pr-8 in-data-input-wrapper:in-data-trailing:in-data-[input-size=sm]:pr-7.5 in-data-input-wrapper:in-data-trailing:in-data-[input-size=xs]:pr-7',
            selectClassName
          )}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden='true'
          className={cx(
            'pointer-events-none absolute text-fg-quaternary',
            sizeConfig.chevronPos,
            sizeConfig.chevron,
            'in-data-input-wrapper:right-0 in-data-input-wrapper:size-4 in-data-input-wrapper:stroke-[2.625px] in-data-input-wrapper:in-data-trailing:in-data-[input-size=sm]:right-3'
          )}
        />
      </div>

      {hint && (
        <HintText className='mt-2' id={hintId}>
          {hint}
        </HintText>
      )}
    </div>
  );
};
